// One typed presentation of the publish check's findings, for every place it shows: the Publish step, the
// Checks panel and the chat notice (DES1 B4). The server's receipt projection (PUB) is the ONLY authority: it
// says the overall state, which findings hold publishing (`holds`), which dependency findings have no fix, and
// whether this person may publish anyway. This module orders, groups and words what it is given; it never
// computes a verdict, a permission or a scanner policy, never reads a secret (the projection carries none),
// and shows no raw rule id or advisory JSON. Nothing here is an "approved" callback.

export type FindingSeverity = 'critical' | 'high' | 'moderate' | 'low' | 'unknown';
export type SecretLabel = 'api_key' | 'private_key' | 'access_token' | 'password';
/** Which tree a finding's file is in: the App's code, or its built files. */
export type FindingArea = 'code' | 'built';

type Common = {
  /** Stable id of this exact finding (the server's, never derived from a value). */
  id: string;
  severity: FindingSeverity;
  /** The server's verdict for THIS finding: true holds publishing. Never recomputed here. */
  holds: boolean;
  /** A prior audited acknowledgment of this finding (dependency only), shown muted; null otherwise. */
  acknowledged: { by: string; at: string } | null;
};
export type SecretFinding = Common & {
  kind: 'secret';
  /** A safe human classification from the projection; null when unknown. Never the rule id. */
  label: SecretLabel | null;
  where: { area: FindingArea; file: string; line: number | null };
};
export type DependencyFinding = Common & {
  kind: 'dependency';
  package: string;
  version: string;
  /** True: a fixed version exists. False: none yet. null: unknown (no "publish anyway", no claim). */
  fixAvailable: boolean | null;
  /** An external advisory URL (https) the projection vouches for, or null. */
  advisoryUrl: string | null;
};
export type CheckFinding = SecretFinding | DependencyFinding;

export type CheckState = 'checking' | 'pass' | 'blocked' | 'expired' | 'incomplete';
/** Who is looking: from the server, never inferred. 'none' sees no detail at all. */
export type CheckAudience = 'owner' | 'editor' | 'member' | 'viewer';

export type CheckReport = {
  state: CheckState;
  audience: CheckAudience;
  /** ISO time of the check; null while checking. */
  checkedAt: string | null;
  findings: readonly CheckFinding[];
  /**
   * The server-authorized, audited "publish anyway" capability for THIS report: only a signed-in owner whose
   * server says so. Absent/false hides every Publish anyway. It is never derived from the audience.
   */
  canAcknowledge: boolean;
  /**
   * The server's FINAL decision for publishing this version (the immutable scan verdict with audited
   * acknowledgments applied): Publish is offered only when this is true. Never computed here.
   */
  publicationEligible: boolean;
  /**
   * An opaque server reference to THIS exact report, handed back untouched with an acknowledgment so the
   * server binds it to the report that was shown, never to a later one. Absent: no acknowledgment action.
   */
  reportRef?: string;
};

export type SummaryKind =
  | 'checking'
  | 'clean'
  | 'warnings'
  | 'held-secret'
  | 'held-dependency'
  | 'held-many'
  | 'expired'
  | 'incomplete'
  | 'held-private'
  | 'hidden';

export type FindingAction = 'fix' | 'publish-anyway';
export type FindingRow = {
  finding: CheckFinding;
  holds: boolean;
  /** 'no-fix': "No fixed version exists yet…"; 'update': "Update to a fixed version."; 'unrated': wait or update. */
  advice: 'secret' | 'update' | 'no-fix' | 'unrated' | 'warning';
  actions: readonly FindingAction[];
};

export type CheckPresentation = {
  summary: { kind: SummaryKind; count: number };
  /** Blocking first, then warnings (server order kept inside each group). Empty for private/hidden. */
  rows: readonly FindingRow[];
  /** The Publish step lists only the summary line when nothing holds; rows are for the Checks panel. */
  primary: 'publish' | 'fix' | 'check-again' | null;
  checkAgain: boolean;
};

const WARN: readonly FindingSeverity[] = ['moderate', 'low'];

function rowFor(finding: CheckFinding, report: CheckReport): FindingRow {
  const holds = finding.holds === true;
  if (finding.kind === 'secret')
    // Secrets always block; the only action is fix. There is no override of any kind.
    return { finding, holds: true, advice: 'secret', actions: ['fix'] };
  if (!holds) return { finding, holds, advice: 'warning', actions: [] };
  const rated = finding.severity !== 'unknown';
  if (finding.fixAvailable === true)
    return { finding, holds, advice: rated ? 'update' : 'unrated', actions: ['fix'] };
  const anyway =
    report.canAcknowledge === true &&
    typeof report.reportRef === 'string' &&
    report.reportRef.length > 0 &&
    finding.fixAvailable === false &&
    finding.acknowledged === null &&
    !WARN.includes(finding.severity);
  return {
    finding,
    holds,
    advice: finding.fixAvailable === false && rated ? 'no-fix' : 'unrated',
    actions: anyway ? ['fix', 'publish-anyway'] : ['fix'],
  };
}

function summaryOf(
  rows: readonly FindingRow[],
  state: CheckState,
  eligible: boolean,
): CheckPresentation['summary'] {
  if (state === 'checking') return { kind: 'checking', count: 0 };
  if (state === 'expired') return { kind: 'expired', count: 0 };
  if (state === 'incomplete') return { kind: 'incomplete', count: 0 };
  // An audited, acknowledged dependency no longer holds: it stays a muted row (server decides, `holds` is
  // the scan's own flag, `acknowledged` the server's record).
  const held = rows.filter((row) => row.holds && row.finding.acknowledged === null);
  if (held.length > 0 || (state === 'blocked' && !eligible)) {
    // The held reason: one secret, one dependency, or a count. A 'blocked' receipt with no listed finding
    // still holds (the server said so); the headline then counts nothing it cannot show.
    if (held.length === 1)
      return {
        kind: held[0].finding.kind === 'secret' ? 'held-secret' : 'held-dependency',
        count: 1,
      };
    return { kind: 'held-many', count: held.length };
  }
  // Only the scan's quiet warnings are "things to look at"; an acknowledged hold is a muted record.
  const warnings = rows.filter((row) => !row.holds).length;
  return warnings > 0 ? { kind: 'warnings', count: warnings } : { kind: 'clean', count: 0 };
}

/** Group and order what the server projected; decide nothing it did not already decide. */
export function presentFindings(report: CheckReport): CheckPresentation {
  if (report.audience === 'viewer')
    return { summary: { kind: 'hidden', count: 0 }, rows: [], primary: null, checkAgain: false };
  if (report.audience === 'member') {
    // "Publishing is held by a security check." with no details, only while the server says it is held.
    const held = report.state !== 'pass';
    return {
      summary: { kind: held ? 'held-private' : 'hidden', count: 0 },
      rows: [],
      primary: null,
      checkAgain: false,
    };
  }
  const rows = report.findings.map((finding) => rowFor(finding, report));
  const isHeld = (row: FindingRow) => row.holds && row.finding.acknowledged === null;
  const ordered = [...rows.filter(isHeld), ...rows.filter((row) => !isHeld(row))];
  const summary = summaryOf(ordered, report.state, report.publicationEligible === true);
  const heldKinds: SummaryKind[] = ['held-secret', 'held-dependency', 'held-many'];
  const primary: CheckPresentation['primary'] = heldKinds.includes(summary.kind)
    ? 'fix'
    : summary.kind === 'expired' || summary.kind === 'incomplete'
      ? 'check-again'
      : (summary.kind === 'clean' || summary.kind === 'warnings') &&
          report.publicationEligible === true
        ? 'publish'
        : null;
  return {
    summary,
    rows: ordered,
    primary,
    checkAgain: summary.kind !== 'checking',
  };
}

/** Human places: the scanner's source/ and output/ prefixes become Code / Built files by the projection. */
export function whereLabel(finding: SecretFinding): { area: FindingArea; text: string } {
  const { area, file, line } = finding.where;
  return { area, text: line === null ? file : `${file}, line ${line}` };
}

/** The chat notice: one compact line and a Review link, no paths; silent on a clean pass. */
export type ChatNotice = null | {
  kind: 'held-secret' | 'held-dependency' | 'held-many' | 'warnings';
  count: number;
};
export function chatNoticeFor(presentation: CheckPresentation): ChatNotice {
  const kind = presentation.summary.kind;
  if (
    kind === 'held-secret' ||
    kind === 'held-dependency' ||
    kind === 'held-many' ||
    kind === 'warnings'
  )
    return { kind, count: presentation.summary.count };
  return null;
}
